import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ToastContainer } from 'react-toastify';
import { UserContext } from './UserContext';
import { api } from '../service/api';
import { authService } from '../service/authApi';
import { setAccessToken, setLegacyContext } from '../service/authRuntime';
import Companies from '../service/Companies';
import { initializeFirebaseSession, signOutFirebase } from '../service/firebaseBridge';
import { resetMatomoUserId, setMatomoUserId } from '../helpers/matomo';
import { normalizeHierarchyLabel } from '../types/hierarchy';
import {
  AxymaSession,
  AxymaSessionDetails,
  AuthStatus,
  LegacyContext,
  LoginDiscovery,
  LoginMethod,
} from '../types/auth';

const LOCAL_CSRF_STORAGE_KEY = 'axyma.local.csrf-token';

const isLocalFrontend = () =>
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

type AuthStore = {
  status: AuthStatus;
  accessToken: string | null;
  csrfToken: string | null;
  session: AxymaSession | AxymaSessionDetails | null;
  discoverLoginMethods: (login: string) => Promise<LoginDiscovery>;
  loginWithPassword: (email: string, senha: string) => Promise<void>;
  startSso: (provider: Exclude<LoginMethod, 'password'>, empresaId: string) => Promise<void>;
  restoreSession: () => Promise<boolean>;
  logout: (all?: boolean) => Promise<void>;
  clearAuthState: () => void;
};

const initialState: AuthStore = {
  status: 'initializing',
  accessToken: null,
  csrfToken: null,
  session: null,
  discoverLoginMethods: async () => ({ methods: [] }),
  loginWithPassword: async () => undefined,
  startSso: async () => undefined,
  restoreSession: async () => false,
  logout: async () => undefined,
  clearAuthState: () => undefined,
};

export const AuthContext = createContext<AuthStore>(initialState);

const csrfFromCookie = () => {
  return document.cookie
    .split('; ')
    .find((item) => item.startsWith('csrfToken='))
    ?.split('=')
    .slice(1)
    .join('=') || null;
};

const csrfForCurrentFrontend = () => {
  if (isLocalFrontend()) {
    return sessionStorage.getItem(LOCAL_CSRF_STORAGE_KEY);
  }

  return csrfFromCookie();
};

const persistLocalCsrf = (token: string) => {
  if (isLocalFrontend()) {
    sessionStorage.setItem(LOCAL_CSRF_STORAGE_KEY, token);
  }
};

const clearLocalCsrf = () => {
  if (isLocalFrontend()) {
    sessionStorage.removeItem(LOCAL_CSRF_STORAGE_KEY);
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    saveUserData,
    setUserData,
    setFirebaseToken,
    setHierarchyLabel,
    saveHierarchyLabel,
    setSessionIdentity,
    clearUserSessionData,
    loadCachedUserData,
    loadCachedHierarchyLabel,
    setLoadingFuncionalities,
  } = useContext(UserContext);
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [accessToken, setToken] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [session, setSession] = useState<AxymaSession | AxymaSessionDetails | null>(null);
  const companiesService = useMemo(() => new Companies(), []);

  const clearAuthState = useCallback(() => {
    clearLocalCsrf();
    setToken(null);
    setCsrfToken(null);
    setSession(null);
    setStatus('anonymous');
    setAccessToken(null);
    setLegacyContext(null);
    setSessionIdentity(null);
    clearUserSessionData();
    resetMatomoUserId();
  }, [clearUserSessionData, setSessionIdentity]);

  const applySession = useCallback(
      async (token: string, csrf: string, baseSession: AxymaSession) => {
      persistLocalCsrf(csrf);

      const { session: details } = await authService.me(token);
      const context: LegacyContext = {
        username: details.userId || baseSession.userId,
        userEmpresa: details.empresaId || baseSession.empresaId,
      };

      setToken(token);
      setCsrfToken(csrf);
      setSession({ ...details, userId: context.username, empresaId: context.userEmpresa });
      setAccessToken(token);
      setLegacyContext(context);
      setSessionIdentity({ userId: context.username, empresaId: context.userEmpresa });

      // Always establish Firebase with this authenticated session. This prevents
      // a persisted Firebase user from a previous browser session being reused.
      const firebaseIdToken = await initializeFirebaseSession(token);
      const legacyAuth = {
        userToken: firebaseIdToken,
        username: context.username,
        userEmpresa: context.userEmpresa,
      };

      setFirebaseToken(legacyAuth.userToken);

      const identity = { userId: context.username, empresaId: context.userEmpresa };
      const cachedUserData = loadCachedUserData(identity);
      const cachedHierarchyLabel = loadCachedHierarchyLabel(identity.empresaId);

      if (cachedUserData) setUserData(cachedUserData);
      if (cachedHierarchyLabel) setHierarchyLabel(cachedHierarchyLabel);

      const [userResponse, hierarchy] = await Promise.all([
        cachedUserData
          ? Promise.resolve(null)
          : api.post('/usuarios/getUser', legacyAuth),
        cachedHierarchyLabel
          ? Promise.resolve(null)
          : companiesService.getHierarchyLabels({
            ...legacyAuth,
            empresa: legacyAuth.userEmpresa,
          }),
      ]);

      if (!cachedUserData) {
        if (!userResponse?.data?.user) throw new Error('Não foi possível carregar os dados do usuário.');
        saveUserData(userResponse.data.user);
      }
      if (!cachedHierarchyLabel) {
        const normalizedHierarchy = normalizeHierarchyLabel(hierarchy);
        if (!normalizedHierarchy) throw new Error('Não foi possível carregar os rótulos da hierarquia.');
        saveHierarchyLabel(normalizedHierarchy);
      }
      setLoadingFuncionalities(false);
      setMatomoUserId(context.username);
      setStatus('authenticated');
    },
    [
      companiesService,
      loadCachedHierarchyLabel,
      loadCachedUserData,
      saveUserData,
      saveHierarchyLabel,
      setHierarchyLabel,
      setSessionIdentity,
      setUserData,
      setFirebaseToken,
      setLoadingFuncionalities,
    ],
  );

  const restoreSession = useCallback(async () => {
    const csrf = csrfForCurrentFrontend();

    if (!csrf) {
      clearAuthState();

      return false;
    }

    try {
      const result = await authService.refresh(csrf);

      await applySession(result.accessToken, csrf, result.session);

      return true;
    } catch {
      clearAuthState();

      return false;
    }
  }, [applySession, clearAuthState]);

  useEffect(() => {
    // The completion route performs the one refresh itself after reading CSRF.
    // Avoid concurrent refreshes because refresh cookies rotate on every use.
    const currentPath = window.location.pathname.replace(/\/+$/, '');

    if (currentPath !== '/auth/sso/complete') {
      void restoreSession();
    }
  }, [restoreSession]);

  const loginWithPassword = useCallback(
    async (email: string, senha: string) => {
      const result = await authService.login(email, senha);

      await applySession(result.accessToken, result.csrfToken, result.session);
    },
    [applySession],
  );

  const startSso = useCallback(
    async (provider: Exclude<LoginMethod, 'password'>, empresaId: string) => {
      const returnTo = new URL('/auth/sso/complete', window.location.origin).toString();
      const { authorizationUrl } = await authService.startSso(provider, empresaId, returnTo);

      window.location.assign(authorizationUrl);
    },
    [],
  );

  const logout = useCallback(async (all = false) => {
    if (accessToken) {
      await authService.logout(accessToken, all);
    }

    await signOutFirebase().catch(() => undefined);

    clearAuthState();
  }, [accessToken, clearAuthState]);

  const store = useMemo(
    () => ({
      status,
      accessToken,
      csrfToken,
      session,
      clearAuthState,
      restoreSession,
      loginWithPassword,
      startSso,
      logout,
      discoverLoginMethods: authService.discover,
    }),
    [
      status,
      accessToken,
      csrfToken,
      session,
      clearAuthState,
      restoreSession,
      loginWithPassword,
      startSso,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={store}>
      <ToastContainer />
      {children}
    </AuthContext.Provider>
  );
};
